import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import AppLayout from '@amzn/meridian/app-layout';
import Alert from '@amzn/meridian/alert';
import Toaster from '@amzn/meridian/toaster';
import Loader from '@amzn/meridian/loader';
import TopBar from './components/TopBar/TopBar';
import Sidebar from './components/Sidebar/Sidebar';
import MessageContainer from './components/MessageContainer/MessageContainer';
import { useMessages } from './hooks/useMessages';
import Theme from '@amzn/meridian/theme';
import customTheme from './assets/custom-theme.json';
import { authService } from './service/authService';
import { AdminRoutes } from './admin/AdminRoutes';

const ChatApp: React.FC<{ userAlias?: string; onUserAliasChange: (alias: string) => void }> = ({
  userAlias,
  onUserAliasChange,
}) => {
  const { messages, isLoading, sendMessage, respondToConfirmation, rateLimitedUntil } =
    useMessages();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; onClose: () => void }>>([]);
  const isBetaEnvironment = window.location.hostname.includes('beta');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const onCloseToast = useCallback(() => {
    setToasts([]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isBetaEnvironment) {
      setToasts([{ id: '1', onClose: onCloseToast }]);
    }
  }, [isBetaEnvironment, onCloseToast]);

  const handleSendMessage = useCallback(
    async (message: string) => {
      const response = await sendMessage(message);
      if (response && response.userAlias && !userAlias) {
        onUserAliasChange(response.userAlias);
      }
    },
    [sendMessage, userAlias, onUserAliasChange],
  );

  return (
    <>
      {isBetaEnvironment && (
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose}>
              This is Beta environment. For Gamma use, please use Gamma
              (https://gamma.ordis.fpa.amazon.dev)
            </Alert>
          )}
        </Toaster>
      )}
      <AppLayout headerComponent={TopBar} sidebarComponent={Sidebar}>
        <TopBar userAlias={userAlias} />
        <Sidebar />
        <MessageContainer
          messages={messages}
          isLoading={isLoading}
          messagesEndRef={messagesEndRef}
          onSendMessage={handleSendMessage}
          onRespondToConfirmation={respondToConfirmation}
        />
      </AppLayout>
    </>
  );
};

const App: React.FC = () => {
  const [userAlias, setUserAlias] = useState<string | undefined>(undefined);
  const [authReady, setAuthReady] = useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Toggle a body class while the admin UI is mounted so global CSS can
  // release the fixed-height / overflow-hidden lock that the chat UI needs.
  // See styles/global.css for the .admin-mode overrides.
  useEffect(() => {
    if (isAdminRoute) {
      document.body.classList.add('admin-mode');
    } else {
      document.body.classList.remove('admin-mode');
    }
    return () => {
      document.body.classList.remove('admin-mode');
    };
  }, [isAdminRoute]);

  // Handle Cognito auth on mount
  useEffect(() => {
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    const initAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);

      // 1. Complete the SPA's Cognito PKCE callback if we're returning
      //    with an authorization code.
      if (urlParams.has('code')) {
        await authService.handleCallback();
      }

      // 2. Ensure the SPA holds valid Cognito tokens (drives user display
      //    and client-side state). Redirects to the Hosted UI if not.
      if (!authService.isAuthenticated()) {
        await authService.login();
        return;
      }

      // 3. Ensure the browser holds the ALB session cookie the backend
      //    depends on. Without this, fetch('/query') is 302'd to Cognito
      //    and fails as a network error. This is a full-page navigation
      //    that returns to '/', so it must run before we mark auth ready.
      //    Skipped on localhost (no ALB) and when a recent bootstrap is
      //    still within the ALB session window.
      if (!isLocalhost && !authService.isAlbSessionFresh()) {
        authService.bootstrapAlbSession();
        return;
      }

      const alias = authService.getUserAlias();
      if (alias) setUserAlias(alias);
      setAuthReady(true);
    };

    initAuth();
  }, []);

  if (!authReady) {
    return (
      <Theme palette={customTheme}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Loader type="circular" size="large" />
        </div>
      </Theme>
    );
  }

  return (
    <Theme palette={customTheme}>
      <Routes>
        <Route path="/admin/*" element={<AdminRoutes />} />
        <Route
          path="*"
          element={<ChatApp userAlias={userAlias} onUserAliasChange={setUserAlias} />}
        />
      </Routes>
    </Theme>
  );
};

export default App;
